import { useEffect, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { Organization } from '../../services/menu/menu.types';
import { IndexableDataStructureWrapper } from './MenuTreeView.types';

/**
 *
 * @param dataStructure DataStructure defining the idKey and buildLabel for each level
 * @param id string that allows this data to be differentiated from other uses of the same hook in other places
 * @param disableLocalStorage prevents tree view states from persisting across session
 * @returns { checkedRows, expandedRows, handleRowClick, handleCheckboxClick, reset } props to pass into the TreeView component and a `reset` function to call to clear the checkboxes/expanded state
 */
const useTreeViewRowStates = (dataStructure: IndexableDataStructureWrapper, id: string, disableLocalStorage: boolean = false) => {
  const checkedRows = useRef<Organization[]>([]);
  const expandedRows = useRef<Organization[]>([]);
  const [hasUpdate, setHasUpdate] = useState(false);

  useEffect(() => {
    const checkedRowsFromStorage = localStorage.getItem(`${id}CheckedRows`);
    const expandedRowsFromStorage = localStorage.getItem(`${id}ExpandedRows`);
    if (checkedRowsFromStorage && !disableLocalStorage) checkedRows.current = JSON.parse(checkedRowsFromStorage);
    if (expandedRowsFromStorage && !disableLocalStorage) expandedRows.current = JSON.parse(expandedRowsFromStorage);
    setHasUpdate(!hasUpdate);
    return () => {
      !disableLocalStorage && localStorage.setItem(`${id}CheckedRows`, JSON.stringify(checkedRows.current));
      !disableLocalStorage && localStorage.setItem(`${id}ExpandedRows`, JSON.stringify(expandedRows.current));
    };
  }, []);

  const handleCheckboxClick = (row: Organization, rowChecked: boolean) => {
    // row.rowChecked = rowChecked;
    const rowsBeingRemoved: any[] = [];

    // if row is checked and doesn't already exist in checkedRows
    if (
      rowChecked &&
      checkedRows.current.findIndex((checkedRow: Organization) => {
        if (
          checkedRow.id === row.id &&
          checkedRow.type === row.type &&
          isEqual(checkedRow.parents, row.parents)
        ) {
          return true;
        } else {
          return false;
        }
      }) === -1
    ) {
      // add row to checkedRows
      checkedRows.current = [...checkedRows.current, row];
      setHasUpdate(!hasUpdate);
      // if row is not checked
    } else if (!rowChecked) {
      // remove row from checkedRows
      const rowToRemove = checkedRows.current.findIndex((checkedRow: Organization) => {
        if (
          checkedRow.id === row.id &&
          checkedRow.type === row.type &&
          isEqual(checkedRow.parents, row.parents)
        )
          return true;
        return false;
      });
      let newCheckedRows = [...checkedRows.current];
      if (rowToRemove !== -1) {
        const rowRemoved = newCheckedRows.splice(rowToRemove, 1)[0];
        // store the row being removed so we can remove it's children
        rowsBeingRemoved.push(rowRemoved);
      }

      while (rowsBeingRemoved.length > 0) {
        const parentRow = rowsBeingRemoved.pop();
        parentRow.rowChecked = false;
        newCheckedRows = newCheckedRows.filter((checkedRow) => {
          // const parentRowParents = dataStructure[parentRow.type].buildParentArray(parentRow);
          const checkedRowParents = dataStructure[checkedRow.type].buildParentArray(checkedRow);
          const rowParents = dataStructure[row.type].buildParentArray(row);
          // const parentRowLevel = dataStructure[parentRow.type].getIndexByType();

          let isEveryParentEquals;
          if (checkedRowParents.length > rowParents.length) {
            isEveryParentEquals = rowParents.every((element: any,  index: number) => {
              return element.id === checkedRowParents[index].id;
            });
          } else {
            isEveryParentEquals = checkedRowParents.every((element: any,  index: number) => {
              return element.id === rowParents[index].id;
            });
          }

          if (isEveryParentEquals) {
            rowsBeingRemoved.push(checkedRow);
            return false;
          }

          return true;
        });
      }

      // remove children of children that are removed
      checkedRows.current = newCheckedRows;
      // checkedRows.current = arrayWithChildrenRemoved;
      setHasUpdate(!hasUpdate);
    }
  };

  const handleRowClick = (row: Organization, rowExpanded: boolean) => {

    if (rowExpanded) {
      expandedRows.current.push(row);
      setHasUpdate(!hasUpdate);
    }
    else {
      const rowToRemove = expandedRows.current.findIndex((expandedRow: Organization) => {
        const expandedRowParents = dataStructure[expandedRow.type].buildParentArray(expandedRow);
        const rowParents = dataStructure[row.type].buildParentArray(row);
        return expandedRow.id === row.id &&
          expandedRow.type === row.type &&
          isEqual(expandedRowParents, rowParents);
      });
      const newExpandedRows = [...expandedRows.current];
      if (rowToRemove !== -1) newExpandedRows.splice(rowToRemove, 1);
      expandedRows.current = newExpandedRows;
      setHasUpdate(!hasUpdate);
    }
  };

  const handleUpwardsDeselectPropagation = (row: any) => {
    const previousIndex = dataStructure[row.type].getIndexByType() - 1;
    if (previousIndex !== -1) {
      let rowParentIndex = checkedRows.current.findIndex((checkedRow) => {
        /** every parent element should match: false = Pizza -> Pan -> Small -> Base === Pizza -> Thin -> Small -> Base */
        const checkedRowParents = dataStructure[checkedRow.type].buildParentArray(checkedRow);
        const checkedRowIndex = dataStructure[checkedRow.type].getIndexByType();
        const rowIndex = dataStructure[row.type].getIndexByType();
        const rowParents = dataStructure[row.type].buildParentArray(row);
        let isParentsEquals = checkedRowParents.every((element: any,  index: number) => {
          return element.id === rowParents[index]?.id;
        });
        return checkedRow.id === rowParents[checkedRowIndex]?.id && checkedRowIndex === rowIndex - 1 && isParentsEquals;
      });
      if (rowParentIndex !== -1) {
        const removedRow = checkedRows.current.splice(rowParentIndex, 1).at(0);
        if (removedRow) removedRow.rowChecked = false;
        handleUpwardsDeselectPropagation(removedRow);
      }
    }
  }

  const handleExternalTableDeselectPropagation = (row: any) => {
    checkedRows.current = checkedRows.current.filter(value => value.id !== row.parents[value.type]);
    setHasUpdate(!hasUpdate);
  }

  const reset = () => {
    checkedRows.current = [];
    expandedRows.current = [];
    setHasUpdate(!hasUpdate);
  };

  const resetLocalStorage = () => {
    checkedRows.current = [];
    expandedRows.current = [];
    localStorage.setItem(`${id}CheckedRows`, JSON.stringify(checkedRows.current));
    localStorage.setItem(`${id}ExpandedRows`, JSON.stringify(expandedRows.current));
    setHasUpdate(!hasUpdate);
  };

  const resetCheckedRows = () => {
    checkedRows.current = [];
    setHasUpdate(!hasUpdate);
  };

  const resetExpandedRows = () => {
    expandedRows.current = [];
    setHasUpdate(!hasUpdate);
  };

  return {
    checkedRows: checkedRows.current,
    expandedRows: expandedRows.current,
    handleRowClick,
    handleCheckboxClick,
    handleUpwardsDeselectPropagation,
    handleExternalTableDeselectPropagation,
    reset,
    resetCheckedRows,
    resetExpandedRows,
    resetLocalStorage,
    hasUpdate
  };
};

export default useTreeViewRowStates;
